import {
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatAccordion } from "@angular/material/expansion";
import { Router } from "@angular/router";
import { firstValueFrom, Subject } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import { AppConstants } from "src/app/constants/constants";
import { Modes } from "src/app/constants/modes";
import { Course } from "src/app/_models/Course";
import { CourseService } from "src/app/_services/course.service";
import { LearnerCourseLogService } from "src/app/_services/learner-course-log.service";
import { PublicUserSessionService } from "src/app/_services/public-user-session.service";
import { LearnerCourseService } from "src/app/_services/learner-course.service";
import { LearnerCourse } from "src/app/_models/LearnerCourse";

@Component({
  selector: "app-initial-assessment",
  templateUrl: "./initial-assessment.component.html",
  styleUrls: ["./initial-assessment.component.scss"],
})
export class InitialAssessmentComponent implements OnInit {
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @Input() courseId: number;
  @Input() learnerCourseLogId: number;
  @Input() learnerId: number;

  public course: Course;
  public learnerCourse: LearnerCourse;
  public practiceContent: string;
  public userFirstName: string;
  public userLastName: string;
  public startLevel: string;
  public practiceMode: Modes = Modes.Practice;
  public trackedMode: Modes = Modes.Tracked;
  siteLanguageCode: string;
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private courseService: CourseService,
    private learnerCoursesService: LearnerCourseService,
    private userSessionService: PublicUserSessionService,
    private lclService: LearnerCourseLogService,
    private router: Router,
    @Inject(LOCALE_ID) protected localeId: string
  ) {
    this.siteLanguageCode = localeId;
  }

  ngOnInit(): void {
    const langId =
      this.siteLanguageCode != AppConstants.WELSH_LANG_CODE
        ? null
        : this.siteLanguageCode;

    if (this.courseId != null) {
      this.getCourseMetadataByIdAndLangId(this.courseId, langId);
      this.trackLearnerCourseVisit(this.learnerCourseLogId);
      this.getCourseStartLevelByIdAndLangId(this.courseId, langId);
    }
  }

  public trackLearnerCourseVisit(lclId: number): void {
    this.lclService
      .trackLearnerCourseVisit$(lclId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {});
  }

  public getCourseMetadataByIdAndLangId(id: number, langId: string): void {
    this.courseService
      .getCourseMetadataByIdAndLangId$(id, langId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((course) => {
        this.course = course;
        this.practiceContent = this.course.metadatas.find(
          (m) => m.name == AppConstants.PRACTICE_ASSESSMENT
        ).value;
        this.userFirstName = sessionStorage.getItem(AppConstants.FIRST_NAME);
        this.userLastName = sessionStorage.getItem(AppConstants.LAST_NAME);
      });
  }

  public getCourseStartLevelByIdAndLangId(id: number, langId: string): void {
    this.learnerCoursesService
      .getLearnerCoursesByLanguageAlt$(langId)
      .pipe(
        catchError(() => {
          return this.router.navigate([AppConstants.DASHBOARD_PAGE]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((learnerCourse: LearnerCourse[]) => {
        this.learnerCourse = learnerCourse.find((lc) => lc.courseId == id);
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  async redirectToContentPlayer(objectId: string = ""): Promise<void> {
    await firstValueFrom(this.userSessionService
      .setIsAllowAccessContent$(AppConstants.TRUE));

    if (!objectId) {
      this.router.navigate(
        [AppConstants.CONTENT_PLAYER_PAGE + `/${this.practiceContent}`],
        {
          queryParams: { mode: Modes.Practice },
        }
      );
      return;
    }

    this.router.navigate([AppConstants.CONTENT_PLAYER_PAGE + `/${objectId}`], {
      queryParams: {
        mode: Modes.Tracked,
        lcLogId: this.learnerCourseLogId,
        lcStartLevel: this.learnerCourse.startLevel,
      },
      queryParamsHandling: AppConstants.MERGE,
    });
  }
}
